import { useEffect, useState } from 'react';
import type { DealDetail } from '@forecastify/shared';
import { Button, Dialog, Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { DateField } from '../date-field';
import type { DealFeeds } from './deal-kit';
import type { DealRecord } from './use-deal-record';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · *Következő lépés* — `phase6/13`.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One action, one date, one person — `PUT /deals/:id/next-step`. ⚠️ The mockup draws no assignee
 * here; the card it opens from does (*Mária Zsigmond* beside the text), so the dialog carries it
 * rather than making the pencil and the avatar two separate edits. Defaults to the deal's owner.
 *
 * ⚠️ Saving an empty text over an existing step CLEARS it — the server takes `''` as *no step* —
 * while saving an empty text with nothing there just closes, as the mockup's *Mentés* does.
 */
export function NextStepDialog({
  open,
  deal,
  record,
  feeds,
  onClose,
}: {
  readonly open: boolean;
  readonly deal: DealDetail;
  readonly record: DealRecord;
  readonly feeds: DealFeeds;
  readonly onClose: () => void;
}) {
  /* `feeds.members` is the picker; the record does the write and swaps the deal for the answer. */
  const t = useT();
  const [text, setText] = useState('');
  const [due, setDue] = useState('');
  const [memberId, setMemberId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* A fresh form, seeded from the current step, every time it opens. */
  useEffect(() => {
    if (!open) return;
    setText(deal.nextStep?.text ?? '');
    setDue(deal.nextStep?.due ?? '');
    setMemberId(deal.nextStep?.memberId ?? deal.ownerMemberId);
    setError(null);
  }, [open, deal.nextStep, deal.ownerMemberId]);

  const save = async (): Promise<void> => {
    const trimmed = text.trim();
    if (trimmed === '' && deal.nextStep === null) {
      onClose();
      return;
    }
    setBusy(true);
    const refusal = await record.write(
      '/next-step',
      'PUT',
      trimmed === ''
        ? { text: '', due: null, memberId: null }
        : {
            text: trimmed,
            due: due === '' ? null : due,
            memberId: memberId === '' ? null : memberId,
          },
    );
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('dealNext.title')}
      description={t('dealNext.hint')}
      closeLabel={t('common.close')}
      width={512}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealNext.text')}>
          <TextInput
            autoFocus
            value={text}
            maxLength={300}
            placeholder={t('dealNext.textPlaceholder')}
            onChange={(event) => {
              setText(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void save();
            }}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealNext.due')}>
            <DateField value={due} onChange={setDue} label={t('dealNext.due')} />
          </Field>
          <Field label={t('dealNext.assignee')}>
            <Select
              searchable
              searchPlaceholder={t('dealOverview.rail.searchPeople')}
              value={memberId}
              onChange={(event) => {
                setMemberId(event.target.value);
              }}
              options={[
                { value: '', label: t('dealNext.nobody') },
                ...feeds.members.map((member) => ({
                  value: member.id,
                  label: member.displayName,
                })),
              ]}
            />
          </Field>
        </div>

        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button variant="ghost" size="lg" onClick={onClose}>
            {t('dealNext.skip')}
          </Button>
          <Button size="lg" disabled={busy} onClick={() => void save()}>
            {t('dealNext.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
